import { z } from '@customerio/hydra-validation';
import type {
  IntegrationsGetSegmentOutLegacyResponse,
  IntegrationsUpsertSegmentOutLegacyRequest,
} from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * The older generation of a workspace's Segment source connector — the record
 * behind `/integrations/segment_out` whose settings are held in the legacy shape.
 *
 * Both generations are the same underlying row. What separates them is which
 * settings block it carries, which the API publishes as `use_javascript` on the
 * `segment_out` read: the current generation stores JS settings, and a record
 * without them is configured through the `segdotcom` endpoints instead. See
 * `isLegacySegmentOut` in `../segment-out-integration/entity`, which is what
 * decides between the two forms.
 *
 * `events` is the difference that matters to the form. The `segment_out` read
 * lists only the selected events; this one answers with the **whole catalogue**
 * mapped to whether each is sent, so it is also where the set of checkboxes comes
 * from. The names arrive without the `email_` prefix the settings store them
 * under, and the write re-adds it, so a value read here can be sent straight back.
 */
export type SegmentOutLegacyIntegration = IntegrationsGetSegmentOutLegacyResponse;

/**
 * The body of a legacy save.
 *
 * `key` is required and is **rewritten on every update**, not just on create — so a
 * save has to send back the key it read. The form shows it read-only for that reason
 * as much as any: there is nothing to edit it to.
 */
export type SegmentOutLegacyInput = IntegrationsUpsertSegmentOutLegacyRequest;

/**
 * One record per workspace, so the detail slot is keyed on a constant rather
 * than an id — the legacy read has no id to key on.
 */
export const SEGMENT_OUT_LEGACY_ID = 'segdotcom';

/**
 * Two fields are checked rather than asserted, because the form depends on both.
 *
 * `events` is what the form indexes: the checkbox list is `Object.keys(events)`,
 * which throws on a missing map instead of reaching the error banner. A missing map
 * is substituted, since a record with no events selected is a real state.
 *
 * `key` is what every save sends back — the endpoint rewrites the write key from the
 * body, and rejects an empty one — so a response without it cannot produce a usable
 * form. It is required here rather than defaulted: failing the read shows the banner,
 * where a blank default would render a form whose first save answers 422.
 *
 * The spec marks both required. A schema that asserts without checking is no help
 * when a response disagrees, and the shared mock (`hydra-msw-mocks`) already answers
 * this endpoint with neither.
 */
const SegmentOutLegacyFromApi = readSchema<SegmentOutLegacyIntegration>(
  z.looseObject({
    key: z.string().min(1),
    events: z
      .record(z.string(), z.boolean())
      .nullish()
      .transform((events) => events ?? {}),
  }),
);

/** @public */
export const segmentOutLegacy = defineEntity<
  SegmentOutLegacyIntegration,
  SegmentOutLegacyInput,
  void,
  undefined,
  'workspace'
>({
  name: 'segment-out-legacy',
  scope: 'workspace',
  pk: () => SEGMENT_OUT_LEGACY_ID,
  schema: { read: SegmentOutLegacyFromApi },
});

export const segmentOutLegacyKeys = segmentOutLegacy.keys;

export function parseSegmentOutLegacy(raw: IntegrationsGetSegmentOutLegacyResponse): SegmentOutLegacyIntegration {
  return segmentOutLegacy.parse(raw);
}

/** The checkbox order: the API returns an unordered map, Ember sorted the names. */
export function segmentOutLegacyEventNames(events: Readonly<Record<string, boolean>>): string[] {
  return Object.keys(events).sort();
}

/**
 * Whether the selection differs from what was loaded.
 *
 * Only the names the saved record answered with are compared. That is Ember's
 * rule and it matters: the map is the whole catalogue, so a name the reader has
 * never touched is present in both and equal, while a name the API stops
 * returning cannot make the form permanently dirty.
 *
 * The write key is not part of it. The API takes the key only on create, so the
 * form shows it read-only and there is nothing for a dirty check to catch.
 */
export function isSegmentOutLegacyDirty(
  events: Readonly<Record<string, boolean>>,
  savedEvents: Readonly<Record<string, boolean>>,
): boolean {
  return Object.keys(savedEvents).some((name) => events[name] !== savedEvents[name]);
}
